// The bundle NAMES live in @cf/shared so the engine, the builder, the report
// and the PowerPoint export cannot drift into calling one bundle three things.
import { PRESET_DISPLAY_LABELS, WRAPPER_VALUES, indexRateRecord, readIndexRate } from '@cf/shared';
/** Own-property read of the per-index derivation strings (see `readIndexRate`). */
function ownIndexBasis(basis, name) {
    if (basis === undefined)
        return undefined;
    return Object.prototype.hasOwnProperty.call(basis, name)
        ? basis[name]
        : undefined;
}
/** Clamp a resolved rate to an optional collar (floor first, then cap). */
function clampToCollar(raw, collar) {
    let v = raw;
    if (collar.floorBps !== undefined)
        v = Math.max(v, collar.floorBps);
    if (collar.capBps !== undefined)
        v = Math.min(v, collar.capBps);
    return v;
}
/** Resolve a SIMPLE (constant-rate) escalation to its annual rate in bps. */
function resolveSimpleBps(esc, ctx) {
    switch (esc.kind) {
        case 'fixed':
            return clampToCollar(esc.rateBps, esc);
        case 'cpiPlus':
            return clampToCollar(ctx.cpiBps + esc.deltaBps, esc);
        case 'rpiPlus':
            return clampToCollar(ctx.rpiBps + esc.deltaBps, esc);
        case 'earningsPlus':
            return clampToCollar(ctx.wageGrowthBps + esc.deltaBps, esc);
        case 'indexPlus': {
            /**
             * An OWN-property read (`readIndexRate`). A plain `[name]` lookup finds
             * inherited members too, so an item escalating on an index called
             * "toString" would have read a FUNCTION here and compounded NaN instead
             * of raising "unknown index". Reserved names are also refused when a set
             * is validated; this is the lock at the point of use.
             */
            const base = readIndexRate(ctx.customIndices, esc.index);
            if (base === undefined) {
                throw new RangeError(`Unknown escalation index: ${esc.index}`);
            }
            return clampToCollar(base + esc.deltaBps, esc);
        }
        case 'none':
            return 0;
        default: {
            const _exhaustive = esc;
            return _exhaustive;
        }
    }
}
/**
 * The effective annual escalation rate (bps) for a given context. For `stepped`
 * schedules this returns the FIRST step's rate — call sites that support stepped
 * escalation must use `resolveEscalationBpsForYear` so each projection year picks
 * its active step. Non-stepped kinds are constant across the projection, so a
 * single resolved rate is exact.
 */
export function resolveEscalationBps(esc, ctx) {
    if (esc.kind === 'stepped') {
        const first = esc.steps[0];
        if (first === undefined)
            return 0;
        return clampToCollar(resolveSimpleBps(first.rate, ctx), esc);
    }
    return resolveSimpleBps(esc, ctx);
}
/**
 * The escalation rate that applies IN a specific projection year — the only
 * kind that varies by year is `stepped` (the active step is the latest whose
 * `fromYear <= year`). Every other kind returns its constant rate, so a caller
 * can use this uniformly in a per-year loop.
 */
export function resolveEscalationBpsForYear(esc, year, ctx) {
    if (esc.kind !== 'stepped')
        return resolveSimpleBps(esc, ctx);
    let active;
    for (const step of esc.steps) {
        if (step.fromYear <= year && (active === undefined || step.fromYear >= active.fromYear)) {
            active = step;
        }
    }
    const base = active === undefined ? 0 : resolveSimpleBps(active.rate, ctx);
    return clampToCollar(base, esc);
}
const PENDING_REVIEW = Object.freeze({ reviewedBy: null, reviewedAt: null });
/**
 * Default preset bundles (nominal, before fees). Values proposed in
 * QUESTIONS.md #1; SHIPPED PENDING HUMAN SIGN-OFF. Equity/property/cash growth
 * per the question; bond figures are conventional placeholders, likewise
 * pending. Fees are NOT baked into presets — fee drag is a per-asset override
 * defaulting to zero, so preset growth stays "before fees".
 *
 * EVERY CLASS SHIPS WITH `yieldBps: 0` (feedback round 7, 2026-08-22). Equity
 * previously carried 2% and bonds 3%, which the engine ADDED to the growth
 * rate: total return was growth + yield, so an adviser who typed 6% on a
 * Stocks & Shares ISA watched it compound at 8%. That was reported four rounds
 * running and answered three times by explaining the yield better; the fourth
 * answer is to stop shipping a rate nobody typed. `growthRateBps` is now the
 * whole of an asset's return before fees, and the box on the form means what
 * it says.
 *
 * The yield MACHINERY is untouched and still load-bearing: `yieldBps` drives
 * GIA dividend tax, lifts the pooled cost basis when reinvested, and adds to
 * total return. It is exposed per asset (Advanced) for holdings that really do
 * throw off income — it simply computes zero until someone asks for it. See
 * `yield-leg-removed.test.ts`, whose last two cases exist to stop this being
 * mistaken for dead code.
 *
 * WHAT THE FIGURES BELOW ARE, after that change (docs/DECISIONS.md D-006):
 *
 * - EQUITY, CASH and PROPERTY keep the exact TOTAL return they shipped with.
 *   Equity's growth absorbed its 2% yield (moderate 5+2 → 7+0); cash and
 *   property never carried one. The point of round 7 was that a typed rate
 *   should mean what it says — it was never that returns should be lower — so
 *   re-levelling them here would be a second, unasked-for change to every
 *   saved plan.
 * - BONDS did NOT get their old total back, and are the one deliberate
 *   re-level: 4%/6%/7% became 3%/4%/5%. Those old figures were the "conventional
 *   placeholders" this very comment has always admitted to, and restoring them
 *   would leave the moderate set with a 1% equity risk premium (7% equity
 *   against 6% bonds), which is not a market that exists. It is also what made
 *   a glidepath moving 30 points of weight cost only 30bps. The premium is now
 *   2/3/4 points across the three bundles, and at 2.5% CPI the moderate bond
 *   figure is ~1.5% real — near the long-run gilt experience, where 3.5% real
 *   was not.
 *
 * Bonds reach the projection only through a model portfolio's equity/bond
 * blend (no wrapper defaults to the bond class), so that re-level touches
 * portfolio-based plans and nothing else.
 *
 * INFLATION AND EARNINGS (feedback round 9, 2026-08-24 — the adviser's own
 * figures, replacing the shared 2.5%/3.5% every bundle used to carry):
 *
 * - CPI now DIFFERS per bundle — conservative 4%, base case 3%, optimistic 2%.
 *   "Conservative" means conservative FOR THE CLIENT, so it carries the
 *   HIGHER inflation: the bundle that erodes purchasing power hardest is the
 *   cautious one.
 * - `wageGrowthBps` is a NOMINAL rate (it escalates earnings directly, like
 *   CPI does spending), but the adviser specified REAL earnings growth —
 *   conservative 0%, base case 1%, optimistic 2%. Each figure below is
 *   therefore CPI + the real rate, and all three land on 400 by arithmetic,
 *   not copy-paste: 400+0, 300+100, 200+200.
 * - RPI keeps its long-standing +1% wedge over CPI (350 was 250+100), so it
 *   moves with each bundle's CPI rather than sitting below it.
 *
 * "Moderate" is LABELLED "Base case" (same internal name — `moderate` is in
 * saved plans and the API contract; renaming the wire value would strand
 * them). "Base case" states what it is without ranking the other two, so the
 * non-advice promise holds.
 */
export const PRESETS = Object.freeze({
    conservative: Object.freeze({
        name: 'conservative',
        label: PRESET_DISPLAY_LABELS.conservative,
        inflationBps: 400,
        rpiBps: 500,
        wageGrowthBps: 400,
        byAssetClass: Object.freeze({
            equity: Object.freeze({ growthBps: 500, yieldBps: 0 }),
            bonds: Object.freeze({ growthBps: 300, yieldBps: 0 }),
            cash: Object.freeze({ growthBps: 150, yieldBps: 0 }),
            property: Object.freeze({ growthBps: 100, yieldBps: 0 }),
        }),
        review: PENDING_REVIEW,
    }),
    moderate: Object.freeze({
        name: 'moderate',
        label: PRESET_DISPLAY_LABELS.moderate,
        inflationBps: 300,
        rpiBps: 400,
        wageGrowthBps: 400,
        byAssetClass: Object.freeze({
            equity: Object.freeze({ growthBps: 700, yieldBps: 0 }),
            bonds: Object.freeze({ growthBps: 400, yieldBps: 0 }),
            cash: Object.freeze({ growthBps: 200, yieldBps: 0 }),
            property: Object.freeze({ growthBps: 300, yieldBps: 0 }),
        }),
        review: PENDING_REVIEW,
    }),
    optimistic: Object.freeze({
        name: 'optimistic',
        label: PRESET_DISPLAY_LABELS.optimistic,
        inflationBps: 200,
        rpiBps: 300,
        wageGrowthBps: 400,
        byAssetClass: Object.freeze({
            equity: Object.freeze({ growthBps: 900, yieldBps: 0 }),
            bonds: Object.freeze({ growthBps: 500, yieldBps: 0 }),
            cash: Object.freeze({ growthBps: 250, yieldBps: 0 }),
            property: Object.freeze({ growthBps: 450, yieldBps: 0 }),
        }),
        review: PENDING_REVIEW,
    }),
});
/** Which asset class a tax wrapper defaults to, for preset resolution. */
export const WRAPPER_DEFAULT_CLASS = Object.freeze({
    cash: 'cash',
    cash_isa: 'cash',
    stocks_isa: 'equity',
    lisa: 'equity', // default: invested LISA; adviser can override to a cash rate
    jisa: 'equity',
    nsi: 'cash', // premium-bond prize fund treated as a cash-like rate, tax-free
    gia: 'equity',
    dc_pension: 'equity',
    db_pension: null, // income promise, not modelled capital
    state_pension: null,
    property: 'property',
    business: null, // no sensible default growth for a private business — adviser sets it
    other_asset: null, // art/vehicles/collectibles: adviser sets growth (may be negative)
});
export const PORTFOLIO_PLANS = Object.freeze([
    Object.freeze({
        id: 'defensive',
        label: 'Defensive 20/80',
        equityWeightBps: 2000,
        volatilityBps: 500,
    }),
    Object.freeze({
        id: 'cautious',
        label: 'Cautious 40/60',
        equityWeightBps: 4000,
        volatilityBps: 800,
    }),
    Object.freeze({
        id: 'balanced',
        label: 'Balanced 60/40',
        equityWeightBps: 6000,
        volatilityBps: 1100,
    }),
    Object.freeze({
        id: 'growth',
        label: 'Growth 80/20',
        equityWeightBps: 8000,
        volatilityBps: 1400,
    }),
    Object.freeze({
        id: 'adventurous',
        label: 'Adventurous 100/0',
        equityWeightBps: 10_000,
        volatilityBps: 1700,
    }),
]);
/** Blend a preset's equity and bond class rates at an explicit equity weight. */
function blendAtWeight(preset, equityWeightBps) {
    const eq = preset.byAssetClass.equity;
    const bond = preset.byAssetClass.bonds;
    const w = equityWeightBps;
    const blend = (a, b) => Math.trunc((a * w + b * (10_000 - w)) / 10_000);
    return {
        growthBps: blend(eq.growthBps, bond.growthBps),
        yieldBps: blend(eq.yieldBps, bond.yieldBps),
    };
}
/** Blend a portfolio's NEUTRAL equity/bond weights against a preset's rates. */
export function portfolioRates(plan, preset) {
    return blendAtWeight(preset, plan.equityWeightBps);
}
/**
 * The equity weight a plan holds at `age`.
 *
 * Clamped at both ends, so an age before the taper gets the neutral weight and
 * an age past it gets the floor — a plan does not keep de-risking past its own
 * end point, and a client already older than `toAge` when the plan is applied
 * starts at the floor rather than somewhere off the line.
 *
 * A glidepath whose ages are inverted or equal is treated as no taper at all
 * rather than dividing by zero: an unusable schedule must not be able to make
 * the projection produce a nonsense weight.
 */
export function equityWeightAtAge(plan, age) {
    const g = plan.glidepath;
    if (g === undefined || g.toAge <= g.fromAge)
        return plan.equityWeightBps;
    if (age <= g.fromAge)
        return plan.equityWeightBps;
    if (age >= g.toAge)
        return g.endEquityWeightBps;
    const travelled = (age - g.fromAge) / (g.toAge - g.fromAge);
    const span = g.endEquityWeightBps - plan.equityWeightBps;
    return Math.round(plan.equityWeightBps + span * travelled);
}
/** A plan's blended rates at `age`, after any glidepath has been applied. */
export function portfolioRatesAtAge(plan, preset, age) {
    return blendAtWeight(preset, equityWeightAtAge(plan, age));
}
/** The wrappers a model portfolio governs — the equity-holding ones. */
const PORTFOLIO_WRAPPERS = Object.freeze(WRAPPER_VALUES.filter((w) => WRAPPER_DEFAULT_CLASS[w] === 'equity'));
/**
 * Turn a glidepath into per-year assumption steps.
 *
 * Deliberately expressed through the EXISTING dated-window mechanism
 * (`AssumptionScheduleStep`, applied per year by `growthDeltaOf` in
 * household.ts) rather than as a new per-year code path in the projection. A
 * glidepath is precisely "these rates, but different in these years", which is
 * what that mechanism already means and already has tests for — and the
 * provenance snapshot records schedule steps, so a de-risked year can be
 * explained from the stored run rather than recomputed.
 *
 * The delta is the change in TOTAL return (growth + yield) and is applied to
 * the growth leg. That keeps each account compounding at the right overall rate
 * as equity gives way to bonds. The income split within the blend is held at
 * the neutral mix, which affects nothing inside an ISA or pension and slightly
 * approximates the income/gain division inside a GIA — a second-order effect on
 * tax only, and the price of not threading a second delta through the engine.
 *
 * Pure and clock-free: the caller supplies the start year and the age at it.
 */
export function glidepathSteps(plan, preset, opts) {
    if (plan.glidepath === undefined)
        return [];
    const neutral = portfolioRates(plan, preset);
    const neutralTotal = neutral.growthBps + neutral.yieldBps;
    const steps = [];
    for (let i = 0; i <= opts.horizonYears; i += 1) {
        const year = opts.startYear + i;
        const rates = portfolioRatesAtAge(plan, preset, opts.currentAge + i);
        const delta = rates.growthBps + rates.yieldBps - neutralTotal;
        // A year that lands exactly on the neutral weight needs no step at all;
        // emitting one would bloat every snapshot with rows that change nothing.
        if (delta === 0)
            continue;
        steps.push({
            id: `glidepath:${plan.id}:${year}`,
            label: `${plan.label} — equity ${Math.round(equityWeightAtAge(plan, opts.currentAge + i) / 100)}% at age ${opts.currentAge + i}`,
            fromYear: year,
            toYear: year,
            growthDeltaBpsByWrapper: Object.fromEntries(PORTFOLIO_WRAPPERS.map((w) => [w, delta])),
        });
    }
    return steps;
}
/** `custom` starts from the moderate bundle, then applies every override. */
function basePreset(name, firmPresets, pinned) {
    const effective = name === 'custom' ? 'moderate' : name;
    if (pinned !== undefined) {
        const b = pinned.bundle;
        return {
            name: effective,
            label: `${pinned.name} v${pinned.versionNumber} (firm)`,
            inflationBps: b.inflationBps,
            rpiBps: b.rpiBps,
            wageGrowthBps: b.wageGrowthBps,
            byAssetClass: { equity: b.equity, bonds: b.bonds, cash: b.cash, property: b.property },
            // Publication is the firm's sign-off; the pending-review warning is for
            // unreviewed PRODUCT figures only.
            review: { reviewedBy: 'firm settings (owner)', reviewedAt: null },
        };
    }
    const firm = firmPresets?.[effective];
    if (firm !== undefined && firm !== null) {
        return {
            name: effective,
            // The firm's own name where it set one (feedback 2026-08-11), else the
            // product label. "(firm)" is kept either way: it is what tells a reader
            // of the report that these are the firm's figures rather than ours, and
            // that stays true — and stays necessary — under a custom name.
            label: firm.displayLabel != null && firm.displayLabel !== ''
                ? `${firm.displayLabel} (firm)`
                : `${PRESETS[effective].label} (firm)`,
            inflationBps: firm.inflationBps,
            rpiBps: firm.rpiBps,
            wageGrowthBps: firm.wageGrowthBps,
            byAssetClass: {
                equity: firm.equity,
                bonds: firm.bonds,
                cash: firm.cash,
                property: firm.property,
            },
            // The owner set these figures deliberately in Firm settings — that IS
            // the firm's sign-off, so the pending-review warning must not fire.
            review: { reviewedBy: 'firm settings (owner)', reviewedAt: null },
        };
    }
    return PRESETS[effective];
}
/**
 * Resolve a preset + overrides into (a) the engine's `HouseholdAssumptions`
 * and (b) a full provenance snapshot. Deterministic and pure.
 */
export function resolveAssumptions(spec) {
    const pinned = spec.pinnedSet;
    const preset = basePreset(spec.preset, spec.firmPresets, pinned);
    const ov = spec.overrides ?? {};
    const entries = [];
    const presetSource = pinned === undefined && spec.preset === 'custom' ? 'default' : 'preset';
    const firmSet = preset.review.reviewedBy === 'firm settings (owner)';
    const firmDefaultFeeBps = pinned !== undefined
        ? pinned.bundle.defaultFeeBps
        : (spec.firmPresets?.[spec.preset === 'custom' ? 'moderate' : spec.preset]?.defaultFeeBps ??
            0);
    /**
     * The firm's own name for the set, where it set one (migration 0028). This
     * is the string a CLIENT reads on the report's assumptions page, so it takes
     * the firm's word for what the set is called; `preset.name` stays the
     * canonical key everywhere behaviour depends on it. Falling back to
     * `preset.name` keeps the un-named case byte-identical to before.
     */
    const firmLabel = spec.firmPresets?.[spec.preset === 'custom' ? 'moderate' : spec.preset]?.displayLabel;
    const namedAs = firmLabel != null && firmLabel !== '' ? firmLabel : null;
    const presetDetail = pinned !== undefined
        ? `${pinned.name} v${pinned.versionNumber} (firm)`
        : spec.preset === 'custom'
            ? `${namedAs ?? 'moderate'} (custom base)${firmSet ? ' (firm)' : ''}`
            : `${namedAs ?? preset.name}${firmSet ? ' (firm)' : ''}`;
    const scalar = (key, override, presetValue) => {
        if (override !== undefined) {
            entries.push({ key, valueBps: override, source: 'adviser-override', detail: null });
            return override;
        }
        entries.push({ key, valueBps: presetValue, source: presetSource, detail: presetDetail });
        return presetValue;
    };
    const inflationBps = scalar('cpi', ov.inflationBps, preset.inflationBps);
    // RPI takes the preset's own RPI figure, exactly like CPI and earnings —
    // `scalar` already records that provenance. It previously carried an extra
    // "RPI proxy (pending)" note, which reached the printed assumptions page of
    // client reports: an unfinished-work marker in a document handed to a
    // client. Whether the preset's figures are signed off is a separate fact,
    // already carried by `reviewPending` on the snapshot and surfaced by the
    // `preset-figures-pending-review` warning, so nothing is lost by removing it.
    const rpiBps = scalar('rpi', ov.rpiBps, preset.rpiBps);
    const wageGrowthBps = scalar('wageGrowth', ov.wageGrowthBps, preset.wageGrowthBps);
    // A model portfolio (if chosen) supplies equity-wrapper growth/yield.
    const plan = spec.portfolio === undefined
        ? null
        : typeof spec.portfolio === 'string'
            ? (PORTFOLIO_PLANS.find((p) => p.id === spec.portfolio) ??
                (() => {
                    throw new RangeError(`Unknown portfolio plan: ${spec.portfolio}`);
                })())
            : spec.portfolio;
    const planRates = plan === null ? null : portfolioRates(plan, preset);
    const growthBpsByWrapper = {};
    const yieldBpsByWrapper = {};
    const feeBpsByWrapper = {};
    for (const w of WRAPPER_VALUES) {
        const cls = WRAPPER_DEFAULT_CLASS[w];
        // Portfolio applies to equity-holding wrappers; other classes keep the preset.
        const usePlan = planRates !== null && cls === 'equity';
        const classRates = usePlan
            ? planRates
            : cls === null
                ? { growthBps: 0, yieldBps: 0 }
                : preset.byAssetClass[cls];
        const classSource = usePlan ? 'portfolio' : presetSource;
        const classDetail = usePlan ? plan.label : cls === null ? 'n/a' : `${presetDetail}:${cls}`;
        const gOverride = ov.growthByWrapper?.[w];
        growthBpsByWrapper[w] = gOverride ?? classRates.growthBps;
        entries.push({
            key: `growth.${w}`,
            valueBps: growthBpsByWrapper[w],
            source: gOverride !== undefined ? 'adviser-override' : classSource,
            detail: gOverride !== undefined ? null : classDetail,
        });
        const yOverride = ov.yieldByWrapper?.[w];
        const yieldValue = yOverride ?? classRates.yieldBps;
        if (yieldValue !== 0 || yOverride !== undefined) {
            yieldBpsByWrapper[w] = yieldValue;
            entries.push({
                key: `yield.${w}`,
                valueBps: yieldValue,
                source: yOverride !== undefined ? 'adviser-override' : classSource,
                detail: yOverride !== undefined ? null : classDetail,
            });
        }
        const fOverride = ov.feeByWrapper?.[w];
        // With no per-asset override, the firm's default fee drag (Firm
        // settings) applies; the product default remains zero.
        const firmFee = firmSet ? firmDefaultFeeBps : 0;
        const feeValue = fOverride ?? firmFee;
        if (feeValue !== 0 || fOverride !== undefined) {
            feeBpsByWrapper[w] = feeValue;
            entries.push({
                key: `fee.${w}`,
                valueBps: feeValue,
                source: fOverride !== undefined ? 'adviser-override' : 'default',
                detail: fOverride !== undefined ? null : firmFee !== 0 ? 'firm default fee' : 'no fee',
            });
        }
    }
    // State-pension escalation: record the RESOLVED rate and its basis, so the
    // one figure most likely to be challenged has a visible source line.
    const spEsc = ov.statePensionEscalation;
    const spBps = statePensionEscalationBps(spEsc, { cpiBps: inflationBps, wageGrowthBps });
    entries.push({
        key: 'statePensionEscalation',
        valueBps: spBps,
        source: spEsc?.kind === 'flat' ? 'adviser-override' : 'default',
        detail: spEsc?.kind === 'flat' ? null : 'triple-lock proxy: max(CPI, earnings, 2.5%)',
    });
    /**
     * Named custom indices, lowest precedence first (§6.1): the pinned set's
     * own indices, then the plan's. A plan index of the SAME NAME replaces the
     * set's rather than adding to it — one name, one rate — and the snapshot
     * says which layer supplied each one, because "School fees 4%" means
     * different things depending on who chose it.
     */
    const pinnedIndices = pinned?.customIndices;
    const customIndices = pinnedIndices === undefined && ov.customIndices === undefined
        ? undefined
        : // Own properties only, and built by `fromEntries` — a spread of a
            // hostile key would define one here, and a later read would find it.
            indexRateRecord([
                ...Object.entries(pinnedIndices ?? {}),
                ...Object.entries(ov.customIndices ?? {}),
            ]);
    if (customIndices !== undefined) {
        for (const [name, valueBps] of Object.entries(customIndices)) {
            const fromPlan = readIndexRate(ov.customIndices, name) !== undefined;
            entries.push({
                key: `customIndex.${name}`,
                valueBps,
                source: fromPlan ? 'adviser-override' : presetSource,
                detail: fromPlan ? null : (ownIndexBasis(pinned?.indexBasis, name) ?? presetDetail),
            });
        }
    }
    // Timed assumption windows: provenanced per step and field so the report can
    // show exactly which years ran on which figures.
    if (ov.schedule !== undefined) {
        for (const step of ov.schedule) {
            const windowLabel = `${step.fromYear}–${step.toYear ?? 'end'}`;
            for (const [key, value] of [
                ['cpi', step.inflationBps],
                ['wageGrowth', step.wageGrowthBps],
                ['rpi', step.rpiBps],
            ]) {
                if (value !== undefined) {
                    entries.push({
                        key: `schedule.${step.id}.${key}`,
                        valueBps: value,
                        source: 'adviser-override',
                        detail: windowLabel,
                    });
                }
            }
            for (const [wrapper, delta] of Object.entries(step.growthDeltaBpsByWrapper ?? {})) {
                if (delta !== undefined) {
                    entries.push({
                        key: `schedule.${step.id}.growthDelta.${wrapper}`,
                        valueBps: delta,
                        source: 'adviser-override',
                        detail: windowLabel,
                    });
                }
            }
        }
    }
    /**
     * A glidepath becomes ordinary dated windows, appended AFTER the adviser's
     * own schedule so an explicit "returns 3% lower in 2027-2031" still wins for
     * that year — the last matching step is the one that applies, and a stated
     * override outranks a policy the plan applies automatically.
     */
    const glidepath = plan === null || plan.glidepath === undefined || spec.glidepathAnchor === undefined
        ? []
        : glidepathSteps(plan, preset, {
            startYear: spec.glidepathAnchor.startYear,
            currentAge: spec.glidepathAnchor.currentAge,
            horizonYears: spec.horizonYears ?? 60,
        });
    for (const step of glidepath) {
        for (const [wrapper, delta] of Object.entries(step.growthDeltaBpsByWrapper ?? {})) {
            entries.push({
                key: `glidepath.${step.fromYear}.growthDelta.${wrapper}`,
                valueBps: delta,
                source: 'portfolio',
                detail: step.label ?? plan.label,
            });
        }
    }
    const schedule = [...(ov.schedule ?? []), ...glidepath];
    const assumptions = {
        inflationBps,
        wageGrowthBps,
        rpiBps,
        growthBpsByWrapper,
        yieldBpsByWrapper,
        feeBpsByWrapper,
        ...(spEsc !== undefined ? { statePensionEscalation: spEsc } : {}),
        ...(customIndices !== undefined ? { customIndices } : {}),
        ...(schedule.length > 0 ? { schedule } : {}),
        ...(spec.horizonYears !== undefined ? { horizonYears: spec.horizonYears } : {}),
    };
    return {
        assumptions,
        snapshot: {
            preset: spec.preset,
            reviewPending: pinned === undefined && spec.preset !== 'custom' && preset.review.reviewedBy === null,
            entries,
            ...(pinned !== undefined
                ? {
                    set: {
                        setId: pinned.setId,
                        versionId: pinned.versionId,
                        versionNumber: pinned.versionNumber,
                        name: pinned.name,
                        contentHash: pinned.contentHash,
                    },
                }
                : {}),
        },
    };
}
export function statePensionEscalationBps(esc, ctx) {
    if (esc?.kind === 'flat')
        return esc.rateBps;
    return Math.max(ctx.cpiBps, ctx.wageGrowthBps, 250);
}
//# sourceMappingURL=assumptions.js.map